import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { 
  ClipboardList, Plus, Search, Filter, Calendar, Grid3X3, List, QrCode,
  RefreshCw, Download, Upload, CheckCircle2, Clock, AlertTriangle, 
  MapPin, User, Eye, FileText, Pause, Play, Edit, Trash2, Loader2
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  completed: { label: 'Terminé', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  in_progress: { label: 'En cours', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock },
  pending: { label: 'En attente', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Clock },
  critical: { label: 'Critique', color: 'bg-red-100 text-red-700 border-red-200', icon: AlertTriangle }
};

const priorityConfig = {
  high: { label: 'Haute priorité', color: 'bg-red-500 text-white' },
  normal: { label: 'Normale', color: 'bg-slate-200 text-slate-700' },
  low: { label: 'Basse', color: 'bg-slate-100 text-slate-500' }
};

const templates = [
  { id: 'livraison', name: 'Livraison chantier', icon: '🏗️', items: ['Vérifier accès', 'Contrôler matériaux', 'Signer bon de livraison', 'Photos documentation'] },
  { id: 'securite', name: 'Contrôle sécurité', icon: '🔒', items: ['Vérifier extincteurs', 'Contrôler issues de secours', 'Tester alarmes', 'Vérifier éclairage'] },
  { id: 'nettoyage', name: 'Nettoyage', icon: '🧹', items: ['Sols', 'Surfaces', 'Vitres', 'Sanitaires', 'Poubelles'] },
  { id: 'inspection', name: 'Inspection véhicule', icon: '🚗', items: ['Niveau huile', 'Pression pneus', 'Freins', 'Éclairage', 'Documents'] },
  { id: 'ouverture', name: 'Ouverture magasin', icon: '🏪', items: ['Désactiver alarme', 'Vérifier caisse', 'Contrôler stock', 'Activer climatisation'] },
  { id: 'custom', name: 'Personnalisé', icon: '✏️', items: [] }
];

export default function FormsPage() {
  const { user, token, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialog, setDetailDialog] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [checklistForm, setChecklistForm] = useState({
    title: '', description: '', location: '', priority: 'normal', items: []
  });
  const [newItem, setNewItem] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchChecklists();
  }, []);

  const fetchChecklists = async () => {
    try {
      const res = await fetch(`${API_URL}/api/checklists`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setChecklists(data);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    await fetchChecklists();
    setSyncing(false);
    toast.success('Synchronisation terminée');
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setChecklistForm({
      title: template.id === 'custom' ? '' : template.name,
      description: '',
      location: '',
      priority: 'normal',
      items: template.items.map(item => ({ text: item, checked: false }))
    });
  };

  const handleAddItem = () => {
    if (!newItem.trim()) return;
    setChecklistForm({
      ...checklistForm,
      items: [...checklistForm.items, { text: newItem.trim(), checked: false }]
    });
    setNewItem('');
  };

  const handleRemoveItem = (index) => {
    setChecklistForm({
      ...checklistForm,
      items: checklistForm.items.filter((_, i) => i !== index)
    });
  };

  const handleCreateChecklist = async () => {
    if (!checklistForm.title.trim()) {
      toast.error('Veuillez ajouter un titre');
      return;
    }
    if (checklistForm.items.length === 0) {
      toast.error('Veuillez ajouter au moins un élément');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/checklists`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(checklistForm)
      });
      if (res.ok) {
        toast.success('Checklist créée');
        setDialogOpen(false);
        setSelectedTemplate(null);
        setChecklistForm({ title: '', description: '', location: '', priority: 'normal', items: [] });
        fetchChecklists();
      }
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleItem = async (checklistId, itemIndex) => {
    try {
      const res = await fetch(`${API_URL}/api/checklists/${checklistId}/toggle/${itemIndex}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        fetchChecklists();
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleUpdateStatus = async (checklistId, status) => {
    try {
      const res = await fetch(`${API_URL}/api/checklists/${checklistId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status })
      });
      if (res.ok) {
        toast.success('Statut mis à jour');
        fetchChecklists();
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDeleteChecklist = async (id) => {
    if (!window.confirm('Supprimer cette checklist ?')) return;
    try {
      const res = await fetch(`${API_URL}/api/checklists/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Checklist supprimée');
        fetchChecklists();
        setDetailDialog(null);
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Statistics
  const stats = {
    total: checklists.length,
    completed: checklists.filter(c => c.status === 'completed').length,
    inProgress: checklists.filter(c => c.status === 'in_progress').length,
    critical: checklists.filter(c => c.items?.some(i => !i.checked) && c.priority === 'high').length
  };

  // Filtered checklists
  const filteredChecklists = checklists.filter(c => {
    const matchSearch = c.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       c.location?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = statusFilter === 'all' || c.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const calculateProgress = (items, sections) => {
    // If we have sections (from execution), use those
    if (sections && sections.length > 0) {
      let total = 0, checked = 0;
      sections.forEach(section => {
        if (section.items) {
          section.items.forEach(item => {
            total++;
            if (item.response === 'conforme' || item.checked) checked++;
          });
        }
      });
      return total > 0 ? Math.round((checked / total) * 100) : 0;
    }
    // Fallback to old items array
    if (!items || items.length === 0) return 0;
    const checked = items.filter(i => i.checked || i.response === 'conforme').length;
    return Math.round((checked / items.length) * 100);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="checklists-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Listes de contrôle</h1>
          <p className="text-slate-500 mt-1">Bienvenue sur votre espace de gestion des checklists</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={handleSync} disabled={syncing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            Synchroniser
          </Button>
          {isAdmin && (
            <>
              <Button variant="outline">
                <Download className="w-4 h-4 mr-2" />Exporter
              </Button>
              <Button variant="outline">
                <Upload className="w-4 h-4 mr-2" />Importer
              </Button>
            </>
          )}
          <Button onClick={() => setDialogOpen(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />Nouvelle checklist
          </Button>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-blue-500 hover:shadow-md transition-shadow">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-xl">
                <ClipboardList className="w-6 h-6 text-blue-600" />
              </div>
              <p className="text-4xl font-bold text-slate-800">{stats.total}</p>
            </div>
            <p className="text-sm text-slate-500 mt-3">Checklists totales</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500 hover:shadow-md transition-shadow">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-100 rounded-xl">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              </div>
              <p className="text-4xl font-bold text-slate-800">{stats.completed}</p>
            </div>
            <p className="text-sm text-slate-500 mt-3">Terminées</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500 hover:shadow-md transition-shadow">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 rounded-xl">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
              <p className="text-4xl font-bold text-slate-800">{stats.inProgress}</p>
            </div>
            <p className="text-sm text-slate-500 mt-3">En cours</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-500 hover:shadow-md transition-shadow">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-red-100 rounded-xl">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <p className="text-4xl font-bold text-slate-800">{stats.critical}</p>
            </div>
            <p className="text-sm text-slate-500 mt-3">Points critiques</p>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher une checklist, un projet, un lieu..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="in_progress">En cours</SelectItem>
                  <SelectItem value="completed">Terminé</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex border rounded-md">
                <Button
                  variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                  size="icon"
                  onClick={() => setViewMode('grid')}
                >
                  <Grid3X3 className="w-4 h-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                  size="icon"
                  onClick={() => setViewMode('list')}
                >
                  <List className="w-4 h-4" />
                </Button>
              </div>
              <Button variant="outline">
                <QrCode className="w-4 h-4 mr-2" />Scanner QR
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Checklists Grid/List */}
      {filteredChecklists.length === 0 && !loading ? (
        <Card className="border-2 border-dashed">
          <CardContent className="p-12 text-center">
            <ClipboardList className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-medium text-slate-600">Aucune checklist</h3>
            <p className="text-slate-400 mt-1">Créez votre première checklist pour commencer</p>
            <Button onClick={() => setDialogOpen(true)} className="mt-4 bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />Créer une checklist
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4' : 'space-y-3'}>
          {filteredChecklists.map((checklist) => {
            const progress = calculateProgress(checklist.items, checklist.sections);
            const StatusIcon = statusConfig[checklist.status]?.icon || Clock;
            
            return viewMode === 'grid' ? (
              <Card key={checklist.id} className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => setDetailDialog(checklist)}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <Badge className={`${statusConfig[checklist.status]?.color} border`}>
                      {statusConfig[checklist.status]?.label}
                    </Badge>
                    {checklist.priority === 'high' && (
                      <Badge className={priorityConfig.high.color}>Haute priorité</Badge>
                    )}
                  </div>
                  <h3 className="font-semibold text-lg mb-2">{checklist.title}</h3>
                  {checklist.location && (
                    <p className="text-sm text-slate-500 flex items-center gap-1 mb-2">
                      <MapPin className="w-3 h-3" />{checklist.location}
                    </p>
                  )}
                  <div className="flex items-center gap-4 text-xs text-slate-400 mb-3">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {format(new Date(checklist.created_at), 'dd MMM yyyy', { locale: fr })}
                    </span>
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />{checklist.created_by_name}
                    </span>
                  </div>
                  <div className="space-y-2">
                    <Progress value={progress} className="h-2" />
                    <p className="text-xs text-slate-500 text-right">{progress}% complété</p>
                  </div>
                  {checklist.status === 'completed' && (
                    <div className="mt-3 p-3 bg-slate-50 rounded-lg text-center">
                      <p className="text-2xl font-bold">{checklist.score || progress}/100</p>
                      <p className="text-xs text-slate-500">Score final</p>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2 mt-4">
                    <Button variant="outline" size="sm" className="flex-1 min-w-[80px]" onClick={(e) => { e.stopPropagation(); setDetailDialog(checklist); }}>
                      <Eye className="w-3 h-3 mr-1" />Détails
                    </Button>
                    {checklist.status === 'completed' ? (
                      <Button size="sm" className="flex-1 min-w-[80px] bg-blue-600 hover:bg-blue-700" onClick={(e) => { e.stopPropagation(); navigate(`/checklist/report/${checklist.id}`); }}>
                        <FileText className="w-3 h-3 mr-1" />Rapport
                      </Button>
                    ) : checklist.status === 'in_progress' ? (
                      <>
                        <Button variant="outline" size="sm" className="flex-1 min-w-[70px]" onClick={(e) => { e.stopPropagation(); handleUpdateStatus(checklist.id, 'pending'); }}>
                          <Pause className="w-3 h-3" />
                        </Button>
                        <Button size="sm" className="flex-1 min-w-[70px] bg-emerald-600 hover:bg-emerald-700" onClick={(e) => { e.stopPropagation(); navigate(`/checklist/${checklist.id}`); }}>
                          <Play className="w-3 h-3" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button variant="outline" size="sm" className="flex-1 min-w-[70px]" onClick={(e) => { e.stopPropagation(); setDetailDialog(checklist); }}>
                          <Edit className="w-3 h-3" />
                        </Button>
                        <Button size="sm" className="flex-1 min-w-[70px] bg-emerald-600 hover:bg-emerald-700" onClick={(e) => { e.stopPropagation(); navigate(`/checklist/${checklist.id}`); }}>
                          <Play className="w-3 h-3" />
                        </Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card key={checklist.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setDetailDialog(checklist)}>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="flex-shrink-0">
                    <StatusIcon className={`w-8 h-8 ${checklist.status === 'completed' ? 'text-emerald-600' : checklist.status === 'in_progress' ? 'text-amber-600' : 'text-blue-600'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold truncate">{checklist.title}</h3>
                      <Badge className={`${statusConfig[checklist.status]?.color} border`}>
                        {statusConfig[checklist.status]?.label}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
                      {checklist.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{checklist.location}</span>}
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{format(new Date(checklist.created_at), 'dd MMM', { locale: fr })}</span>
                    </div>
                  </div>
                  <div className="w-32">
                    <Progress value={progress} className="h-2" />
                    <p className="text-xs text-slate-500 text-right mt-1">{progress}%</p>
                  </div>
                  <Button variant="outline" size="sm">
                    <Eye className="w-4 h-4" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
          
          {/* Create new card */}
          <Card className="border-2 border-dashed border-blue-200 hover:border-blue-400 transition-colors cursor-pointer bg-blue-50/50" onClick={() => setDialogOpen(true)}>
            <CardContent className="p-8 flex flex-col items-center justify-center text-center h-full min-h-[250px]">
              <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mb-4">
                <Plus className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="font-semibold text-blue-700">Créer une nouvelle checklist</h3>
              <p className="text-sm text-blue-500 mt-1">Choisissez un modèle ou créez-en un personnalisé</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Create Checklist Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setSelectedTemplate(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedTemplate ? `Nouvelle checklist - ${selectedTemplate.name}` : 'Choisir un modèle'}
            </DialogTitle>
          </DialogHeader>
          
          {!selectedTemplate ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 py-4">
              {templates.map(template => (
                <Card 
                  key={template.id} 
                  className="cursor-pointer hover:shadow-md hover:border-blue-300 transition-all"
                  onClick={() => handleSelectTemplate(template)}
                >
                  <CardContent className="p-4 text-center">
                    <div className="text-4xl mb-2">{template.icon}</div>
                    <h4 className="font-medium">{template.name}</h4>
                    <p className="text-xs text-slate-500 mt-1">{template.items.length || '∞'} éléments</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Titre *</Label>
                <Input
                  value={checklistForm.title}
                  onChange={(e) => setChecklistForm({ ...checklistForm, title: e.target.value })}
                  placeholder="Ex: Inspection site client"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={checklistForm.description}
                  onChange={(e) => setChecklistForm({ ...checklistForm, description: e.target.value })}
                  placeholder="Description optionnelle..."
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Lieu</Label>
                  <Input
                    value={checklistForm.location}
                    onChange={(e) => setChecklistForm({ ...checklistForm, location: e.target.value })}
                    placeholder="Ex: 12 Rue des Lilas, Paris"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Priorité</Label>
                  <Select value={checklistForm.priority} onValueChange={(v) => setChecklistForm({ ...checklistForm, priority: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Basse</SelectItem>
                      <SelectItem value="normal">Normale</SelectItem>
                      <SelectItem value="high">Haute</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Éléments de la checklist</Label>
                <div className="flex gap-2">
                  <Input
                    value={newItem}
                    onChange={(e) => setNewItem(e.target.value)}
                    placeholder="Ajouter un élément..."
                    onKeyDown={(e) => e.key === 'Enter' && handleAddItem()}
                  />
                  <Button type="button" onClick={handleAddItem}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {checklistForm.items.map((item, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                      <span className="flex-1">{item.text}</span>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleRemoveItem(index)}>
                        <Trash2 className="w-3 h-3 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            {selectedTemplate && (
              <Button variant="outline" onClick={() => setSelectedTemplate(null)}>Retour</Button>
            )}
            <Button variant="outline" onClick={() => { setDialogOpen(false); setSelectedTemplate(null); }}>Annuler</Button>
            {selectedTemplate && (
              <Button onClick={handleCreateChecklist} disabled={submitting} className="bg-blue-600 hover:bg-blue-700">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Créer la checklist'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!detailDialog} onOpenChange={() => setDetailDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{detailDialog?.title}</span>
              <Badge className={`${statusConfig[detailDialog?.status]?.color} border`}>
                {statusConfig[detailDialog?.status]?.label}
              </Badge>
            </DialogTitle>
          </DialogHeader>
          {detailDialog && (
            <div className="space-y-4">
              {detailDialog.description && (
                <p className="text-slate-600">{detailDialog.description}</p>
              )}
              {detailDialog.location && (
                <p className="text-sm text-slate-500 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />{detailDialog.location}
                </p>
              )}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Progression</Label>
                  <span className="text-sm font-medium">{calculateProgress(detailDialog.items, detailDialog.sections)}%</span>
                </div>
                <Progress value={calculateProgress(detailDialog.items, detailDialog.sections)} className="h-3" />
              </div>
              <div className="space-y-2">
                <Label>Éléments ({detailDialog.items?.filter(i => i.checked).length}/{detailDialog.items?.length})</Label>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {detailDialog.items?.map((item, index) => (
                    <div 
                      key={index} 
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${item.checked ? 'bg-emerald-50 border-emerald-200' : 'bg-white hover:bg-slate-50'}`}
                      onClick={() => handleToggleItem(detailDialog.id, index)}
                    >
                      <Checkbox checked={item.checked} />
                      <span className={item.checked ? 'line-through text-slate-400' : ''}>{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="destructive" size="sm" onClick={() => handleDeleteChecklist(detailDialog?.id)}>
              <Trash2 className="w-4 h-4 mr-1" />Supprimer
            </Button>
            <Button variant="outline" onClick={() => setDetailDialog(null)}>Fermer</Button>
            {detailDialog?.status !== 'completed' && (
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => { handleUpdateStatus(detailDialog.id, 'completed'); setDetailDialog(null); }}>
                <CheckCircle2 className="w-4 h-4 mr-1" />Marquer terminé
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
